/**
 * Placement resolution — the pure platform × placement × mediaKind
 * capability model layered on top of `placement-matrix.ts`. Lives NEXT TO
 * the matrix (not in `src/lib/posts/`) so it can be re-exported through the
 * Deno edge stub the same way the matrix itself is: ONE implementation
 * serves the client picker/preview AND the server-side publisher/dispatcher
 * — no hand-copied server derivation to drift out of sync.
 *
 * Two independent axes, deliberately NOT collapsed into one flag:
 *
 *   - SELECTABILITY (`allowedPlacements` / `placementDisabledReason`): can
 *     this placement even render this media kind? A `feed` row accepts
 *     text everywhere in principle (`PLACEMENT_MEDIA_KINDS`), but TikTok has
 *     no text-only post_mode —
 *     `PUBLISHER_MEDIA_KINDS` is the honest, per-publisher narrowing on top
 *     of the matrix's platonic per-placement capability.
 *   - PUBLISHABILITY (`PUBLISHABLE_PLACEMENTS`): can Mora publish to this
 *     placement AT ALL today, independent of media kind (e.g. Instagram
 *     Stories before Task A5 ships the STORIES container). The UI omits
 *     non-publishable placements entirely rather than showing them
 *     disabled-with-reason — see Decision #5 in the plan's audit trail.
 *
 * HARD CONSTRAINT: same as placement-matrix.ts — React-free, DOM-free,
 * Node-free, `@/`-alias-free, relative imports only (with explicit `.ts`
 * extensions so Deno can resolve this module directly through
 * `supabase/functions/_shared/placement-matrix.ts`).
 */
import {
  type Placement,
  type PlacementRow,
  type Platform,
  getPlacementsForPlatform,
  toMatrixPlatform,
} from './placement-matrix.ts';

export type MediaKind = 'image' | 'video' | 'carousel' | 'text';

/** What the PLACEMENT accepts in principle, independent of any one publisher. */
export const PLACEMENT_MEDIA_KINDS: Record<Placement, readonly MediaKind[]> = {
  feed: ['image', 'video', 'carousel', 'text'],
  reel: ['video'],
  story: ['image', 'video'],
  short: ['video'],
  pin: ['image', 'video'],
  landscape: ['video'],
  document: ['image'],
  // TikTok-only today: the Content Posting API's PHOTO Direct Post path
  // (image/carousel), distinct from the video-only `feed` row above. No
  // 'text' — there is no post_mode that publishes a caption with zero media.
  photo: ['image', 'carousel'],
};

/**
 * What OUR publisher for (platform, placement) actually supports TODAY — the
 * honest gate narrowing `PLACEMENT_MEDIA_KINDS`. A platform/placement pair is
 * only listed here when its real support is NARROWER than the placement's
 * platonic capability; absence means "no extra restriction, defer to
 * `PLACEMENT_MEDIA_KINDS`."
 */
export const PUBLISHER_MEDIA_KINDS: Partial<
  Record<Platform, Partial<Record<Placement, readonly MediaKind[]>>>
> = {
  // TikTok photo-mode task: post-to-tiktok now routes image/carousel media
  // through the Content Posting API's PHOTO Direct Post path (PULL_FROM_URL,
  // up to 35 images — see shared/tiktok-post-params.ts). Text-only stays
  // unsupported (no post_mode covers a caption with zero media), so this
  // still narrows PLACEMENT_MEDIA_KINDS.feed (which includes 'text').
  tiktok: { feed: ['image', 'video', 'carousel'] },
  linkedin: { feed: ['image', 'video', 'carousel', 'text'] },
  // Since Nov 2023 the IG Graph API has no standalone feed-video container —
  // every single video ships as media_type=REELS (share_to_feed governs feed
  // visibility). A "Feed post" video placement is therefore a fiction; video
  // belongs to the reel row only. (Meta content-publishing changelog.)
  instagram: { feed: ['image', 'carousel'] },
  // Threads keeps 'text' — it is the ONLY platform Mora targets where a
  // text-only post is a real API shape (media_type=TEXT), so narrowing it out
  // here would silently delete its one unique capability. Verified:
  // developers.facebook.com/documentation/threads/posts
  threads: { feed: ['image', 'video', 'carousel', 'text'] },
};

/**
 * The publishability ceiling — the axis separate from selectability. A
 * placement absent (or not listed) here is never offered by the picker even
 * when its media-kind capability is fine, because Mora can't actually
 * publish to it yet. `instagram` gains `'story'` when Task A5 ships;
 * `facebook` gains `'reel','story'` when Task A6 ships.
 */
export const PUBLISHABLE_PLACEMENTS: Record<Platform, readonly Placement[]> = {
  instagram: ['feed', 'reel', 'story'],
  facebook: ['feed', 'reel', 'story'],
  tiktok: ['feed', 'photo'],
  linkedin: ['feed'],
  x: [],
  youtube: [],
  pinterest: [],
  threads: [],
};

/**
 * Chrome placements the author must CHOOSE; the legacy "video goes to the first
 * chrome row" derivation never lands on them. Facebook video has always posted
 * as a classic /videos feed video, and a 16:9 clip must not silently become a
 * Reel (which Facebook rejects outside 9:16) the day Reels become publishable.
 */
const OPT_IN_PLACEMENTS: Partial<Record<Platform, readonly Placement[]>> = {
  facebook: ['reel', 'story'],
};

/** User-facing sentence-case reasons for a placement-level media-kind mismatch. */
const PLACEMENT_KIND_REASON: Partial<Record<Placement, string>> = {
  reel: 'Reels require video',
  short: 'Shorts require video',
  landscape: 'Landscape videos only',
  document: 'Documents require an image',
  story: 'Stories accept an image or video',
  pin: 'Pins accept an image or video',
};

/** User-facing sentence-case reasons for a publisher-level media-kind mismatch. */
const PUBLISHER_KIND_REASON: Partial<Record<Platform, Partial<Record<Placement, string>>>> = {
  tiktok: { feed: 'TikTok posts need an image, video, or carousel — no text-only posts' },
  instagram: { feed: 'Instagram publishes video as a Reel' },
};

const PUBLISHER_KIND_REASON_BY_KIND: Partial<
  Record<Platform, Partial<Record<Placement, Partial<Record<MediaKind, string>>>>>
> = {
  instagram: { feed: { text: 'Instagram posts need an image or a carousel, no text-only posts' } },
};

/**
 * True intersection of a placement's platonic capability with any
 * publisher-specific narrowing for that (platform, placement) pair.
 */
function acceptedMediaKinds(platform: Platform, placement: Placement): readonly MediaKind[] {
  const placementKinds = PLACEMENT_MEDIA_KINDS[placement];
  const publisherKinds = PUBLISHER_MEDIA_KINDS[platform]?.[placement];
  if (!publisherKinds) return placementKinds;
  return placementKinds.filter((kind) => publisherKinds.includes(kind));
}

/**
 * Why (platform, placement) can't take `mediaKind`, or `null` if it can.
 * Checks the placement-level constraint first — "Reels require video" is
 * more informative and more fundamental than a publisher-specific reason —
 * then the publisher-level gate.
 */
export function placementDisabledReason(
  platform: string,
  placement: Placement,
  mediaKind: MediaKind,
): string | null {
  const p = toMatrixPlatform(platform);
  if (!p) return null;
  return disabledReasonFor(p, placement, mediaKind);
}

function disabledReasonFor(
  platform: Platform,
  placement: Placement,
  mediaKind: MediaKind,
): string | null {
  if (!PLACEMENT_MEDIA_KINDS[placement].includes(mediaKind)) {
    return PLACEMENT_KIND_REASON[placement] ?? `This placement doesn't accept ${mediaKind}`;
  }

  const publisherKinds = PUBLISHER_MEDIA_KINDS[platform]?.[placement];
  if (publisherKinds && !publisherKinds.includes(mediaKind)) {
    return (
      PUBLISHER_KIND_REASON_BY_KIND[platform]?.[placement]?.[mediaKind] ??
      PUBLISHER_KIND_REASON[platform]?.[placement] ??
      'Not supported for this media type yet'
    );
  }

  return null;
}

/**
 * Every matrix row for `platform` with its selectability verdict for
 * `mediaKind`. Includes placements that AREN'T publishable yet (e.g.
 * Instagram Story pre-A5) — callers needing the publishability axis filter
 * through `PUBLISHABLE_PLACEMENTS` themselves; this module never silently
 * drops a row.
 */
export function allowedPlacements(
  platform: string,
  mediaKind: MediaKind,
): { row: PlacementRow; disabledReason: string | null }[] {
  const p = toMatrixPlatform(platform);
  if (!p) return [];
  return getPlacementsForPlatform(p).map((row) => ({
    row,
    disabledReason: disabledReasonFor(p, row.placement, mediaKind),
  }));
}

/**
 * Resolve the effective placement row for a post. An explicit `override`
 * wins when it names a real row for the platform AND that row accepts
 * `mediaKind` (the true platform×placement×mediaKind intersection, not just
 * the placement's platonic capability); otherwise falls back to today's
 * legacy derivation — video → first chrome row, else primary — so
 * unmigrated posts render pixel-identical to before this module existed
 * (mirrors PostBody.tsx's prior inline ternary at ~1358-1364).
 */
export function resolvePlacement(input: {
  platform: string;
  mediaKind: MediaKind;
  override?: string | null;
}): PlacementRow | undefined {
  const platform = toMatrixPlatform(input.platform);
  if (!platform) return undefined;
  const rows = getPlacementsForPlatform(platform);

  if (input.override) {
    const overrideRow = rows.find((row) => row.placement === input.override);
    // Same publish-truth rule as the video derivation below: on a platform
    // Mora publishes to, a stored override naming an UNPUBLISHABLE placement
    // is ignored (falls to the derivation) — otherwise the preview would
    // honor a choice the trigger reports as ignored and the publisher can't
    // execute. Preview-only platforms (no publishable placements) are exempt.
    const publishableForOverride = PUBLISHABLE_PLACEMENTS[platform];
    if (
      overrideRow &&
      acceptedMediaKinds(platform, overrideRow.placement).includes(input.mediaKind) &&
      (publishableForOverride.length === 0 ||
        publishableForOverride.includes(overrideRow.placement))
    ) {
      return overrideRow;
    }
  }

  if (input.mediaKind === 'video') {
    // Preview must match publish: on a platform Mora can publish to, only a
    // PUBLISHABLE chrome row may win the video derivation. Facebook video
    // previously derived the (unpublishable) reel row and previewed reel
    // chrome + safe zones while the publisher shipped a classic /videos feed
    // video — a preview lie (audit V4, 2026-07-10). Platforms with NO
    // publishable placements at all (x, youtube, …) are preview-only
    // surfaces; they keep the pure legacy derivation.
    const publishable = PUBLISHABLE_PLACEMENTS[platform];
    const optIn = OPT_IN_PLACEMENTS[platform] ?? [];
    const chromeRow = rows.find(
      (row) =>
        row.hasChrome &&
        !optIn.includes(row.placement) &&
        (publishable.length === 0 || publishable.includes(row.placement)),
    );
    if (chromeRow) return chromeRow;
  }

  // Non-video media prefers a row that isn't video-locked, mirroring the
  // video branch above. Exists for TikTok: its primary `feed` row is
  // `videoOnly: true` and listed first, so the terminal `rows[0]` fallback
  // below would silently resolve a photo carousel onto the video-locked
  // 9:16-only window instead of the wider `photo` row added for exactly
  // this case. On every other platform rows[0] is already non-video-only,
  // so this is a no-op there.
  if (input.mediaKind !== 'video') {
    const nonVideoRow = rows.find((row) => !row.videoOnly);
    if (nonVideoRow) return nonVideoRow;
  }

  // Terminal fallback is the platform's primary row REGARDLESS of mediaKind —
  // deliberate legacy parity (an image on YouTube resolved to the video-only
  // `short` row before this module existed, and preview must keep matching
  // publish for unmigrated posts). Selectability gating is the UI's job via
  // `allowedPlacements`; revisit when Task A3 makes PostBody consume this.
  return rows[0];
}

/**
 * An override is "honored" by the picker/preview only when `resolvePlacement`
 * accepts it (real row, satisfies `mediaKind`) AND it names a placement in
 * the effective `publishablePlacements` ceiling passed by the caller — which
 * may be narrower than the static `PUBLISHABLE_PLACEMENTS[platform]` table
 * (Task A5's per-CONNECTION narrowing, e.g. Instagram Story on a non-Business
 * connection). Extracted so `PlacementPicker`'s trigger label and
 * `PostBody`'s preview surface can never diverge on what counts as
 * "honored" — see Decision #5's "the trigger must never claim a placement
 * its own menu omits," now generalized to every honored-override consumer.
 */
export function isPlacementOverrideHonored(
  platform: string,
  mediaKind: MediaKind,
  override: string | undefined | null,
  publishablePlacements: readonly Placement[],
): boolean {
  if (!override) return false;
  const row = resolvePlacement({ platform, mediaKind, override });
  return !!row && row.placement === override && publishablePlacements.includes(row.placement);
}

/**
 * Account type as stored on `user_connections.account_type` (case-
 * insensitive, `null`/`undefined`/unrecognized all treated the same as "not
 * confirmed Business/Creator" — see `connectionSupportsPlacement`).
 */
export type ConnectionAccountType = string | null | undefined;

/**
 * The full PROFESSIONAL account-type value set across Meta's surfaces
 * (verified against Meta docs, 2026-07-11): the IG User `account_type`
 * field returns `BUSINESS` | `MEDIA_CREATOR` | `PERSONAL` (per
 * developers.facebook.com's IG User reference / `me?fields=account_type`),
 * while Mora's own FB-Login connect path writes `BUSINESS` and some Meta
 * surfaces/docs spell Creator without the `MEDIA_` prefix. Story publishing
 * is allowed for ANY professional account — Business AND Creator alike — so
 * the gate accepts every professional spelling and fails closed only on
 * PERSONAL/null/unknown.
 */
const PROFESSIONAL_ACCOUNT_TYPES: ReadonlySet<string> = new Set([
  'BUSINESS',
  'CREATOR',
  'MEDIA_CREATOR',
]);

/**
 * Task A5 (ENG REVIEW Decision #24): per-CONNECTION narrowing layered on top
 * of the static `PUBLISHABLE_PLACEMENTS` ceiling. Today the only
 * connection-level gate is Instagram Stories, which the Graph API only
 * allows a PROFESSIONAL (Business or Creator) account to publish via the
 * FB-Login flow — every other (platform, placement) pair defers entirely to
 * the static table (returns `true` unconditionally). `accountType` is
 * normalized upper-case against `PROFESSIONAL_ACCOUNT_TYPES`; an
 * unset/unrecognized value is NOT assumed professional — this fails CLOSED
 * (blocks) rather than silently trusting an unverified connection, matching
 * the A5 preflight decision that an account whose type turns out unknown
 * pauses rather than guesses.
 */
export function connectionSupportsPlacement(
  platform: string,
  placement: Placement,
  accountType: ConnectionAccountType,
): boolean {
  const p = toMatrixPlatform(platform);
  if (!p) return false;
  if (p === 'instagram' && placement === 'story') {
    const normalized = (accountType ?? '').toString().trim().toUpperCase();
    return PROFESSIONAL_ACCOUNT_TYPES.has(normalized);
  }
  return true;
}

/**
 * `PUBLISHABLE_PLACEMENTS[platform]` narrowed by what THIS connection can
 * actually publish today (Task A5) — the per-connection ceiling
 * `PlacementPicker`'s `publishablePlacements` prop and the server-side
 * readiness check both consume. Never flips a global flag (Decision #24) —
 * two connections to the same platform can see two different ceilings.
 */
export function connectionPublishablePlacements(
  platform: string,
  accountType: ConnectionAccountType,
): readonly Placement[] {
  const p = toMatrixPlatform(platform);
  if (!p) return [];
  return PUBLISHABLE_PLACEMENTS[p].filter((placement) =>
    connectionSupportsPlacement(p, placement, accountType),
  );
}

/** User-facing sentence-case reasons for a connection-level (account-type) gate. */
const CONNECTION_PLACEMENT_REASON: Partial<Record<Platform, Partial<Record<Placement, string>>>> = {
  instagram: {
    story:
      'Instagram Stories require a professional (Business or Creator) account — reconnect Instagram to update the connected account type.',
  },
};

/**
 * Why THIS connection (independent of media kind) can't publish to
 * `placement`, or `null` if it can. Mirrors `placementDisabledReason`'s
 * shape but for the connection-level axis (Task A5) — consumed by
 * `evaluatePlatformReadiness`'s server-side gate.
 */
export function connectionPlacementDisabledReason(
  platform: string,
  placement: Placement,
  accountType: ConnectionAccountType,
): string | null {
  const p = toMatrixPlatform(platform);
  if (!p) return null;
  if (connectionSupportsPlacement(p, placement, accountType)) return null;
  return (
    CONNECTION_PLACEMENT_REASON[p]?.[placement] ??
    'This placement is not available for the connected account.'
  );
}
